import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { PasswordField } from "@/components/PasswordUI";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      nav(u.role === "candidate" ? "/candidate" : (u.role === "admin" || u.role === "super_admin") ? "/admin" : "/employer");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-surface border-r border-white/10 relative overflow-hidden">
        <div className="absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-azure/10 blur-[100px]" aria-hidden />
        <Logo />
        <div className="relative">
          <h2 className="font-display font-bold tracking-tight text-3xl max-w-sm">Evidence-based technical hiring, end to end.</h2>
          <p className="mt-4 text-muted max-w-sm">Sign in to your candidate application, employer workspace or admin portal.</p>
        </div>
        <p className="label-mono text-faint relative">teamassembly.io</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-display font-bold text-2xl mb-1">Sign in</h1>
          <p className="text-sm text-muted mb-8">Access your Team Assembly account.</p>
          {err && <div className="mb-4 rounded-lg border border-signal/30 bg-signal/10 px-3 py-2 text-sm text-signal" data-testid="login-error">{err}</div>}
          <label className="label-mono text-faint">Email</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 mb-4 w-full rounded-lg border border-white/12 bg-surface px-3.5 py-2.5 text-sm text-ink focus:border-azure focus:outline-none focus:ring-2 focus:ring-azure/40 transition-colors duration-200" />
          <label className="label-mono text-faint">Password</label>
          <div className="mt-1.5 mb-2"><PasswordField value={password} onChange={(e) => setPassword(e.target.value)} testId="login-password" autoComplete="current-password" /></div>
          <div className="mb-6 text-right">
            <Link to="/forgot-password" data-testid="forgot-link" className="text-xs text-azure hover:underline">Forgot password?</Link>
          </div>
          <button disabled={busy} data-testid="login-submit"
            className="w-full rounded-full bg-azure py-3 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <p className="mt-6 text-sm text-muted text-center">
            No account? <Link to="/register" className="text-azure hover:underline">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
